import { ReactNode, useCallback, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useCopyToClipboard } from "react-use";
import { Button, Dropdown } from "antd";

import { poppins } from "@/components/common/fonts";
import { MyProjectItemDetail } from "@/types/dashboard.api";
import { IconClose, IconCode, IconCopy } from "@/components/common/Icons";
import { useNotification } from "@/hooks/useNotification";

export const ShareVideoDropdown = ({
  project,

  children,
}: {
  project?: MyProjectItemDetail;

  children?: ReactNode;
}) => {
  const [isOpened, setIsOpened] = useState(false);

  return (
    <StyledDropdown
      open={isOpened}
      trigger={["click"]}
      onOpenChange={(open) => setIsOpened(open)}
      dropdownRender={() => (
        <DropdownContent project={project} onClose={() => setIsOpened(false)} />
      )}
      destroyPopupOnHide
      placement="bottomRight"
      align={{
        offset: [0, 13],
      }}
    >
      {children}
    </StyledDropdown>
  );
};

const StyledDropdown = styled(Dropdown)``;

const DropdownContent = ({
  project,
  onClose,
}: {
  project?: MyProjectItemDetail;
  onClose?: () => void;
}) => {
  const router = useRouter();

  const { t } = useTranslation("common");

  const [, copyToClipboard] = useCopyToClipboard();

  const { contextHolder, notify } = useNotification();

  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}${router.asPath}`;

    copyToClipboard(url);

    notify.success(
      t(
        "The shared link has been copied to your clipboard. Anyone with the link can access it.",
      ),
    );
  }, [router, notify, copyToClipboard, t]);

  const handleCopyEmbedCode = useCallback(() => {
    if (!project?.download_url || !project?.name) {
      return;
    }

    const code = `<div style="position: relative; overflow: hidden; aspect-ratio: 1920/1080"><iframe src="${project?.download_url}" loading="lazy" title="DeepbrainAiStudios-${project?.name}" allow="encrypted-media; fullscreen;" style="position: absolute; width: 100%; height: 100%; top: 0; left: 0; border: none; padding: 0; margin: 0; overflow:hidden;"></iframe></div>`;

    copyToClipboard(code);

    notify.success(
      t(
        "The embed code has been copied to clipboard. Anyone with link can access it",
      ),
    );
  }, [project, copyToClipboard, notify, t]);

  return (
    <ContentContainer>
      <IconClose
        css={`
          position: absolute;
          right: 20px;
          top: 25px;
          width: 10px;
          height: 10px;
          cursor: pointer;

          transition: opacity 0.2s;

          &:hover {
            opacity: 0.5;
          }
        `}
        viewBox="0 0 18 18"
        onClick={onClose}
      />

      <Title>{t("Share this video")}</Title>
      <Description>
        {t(
          "Share this video with a wider audience and enjoy the experience together!",
        )}
      </Description>
      <ShareButtons>
        {!!project?.download_url && !!project?.name && (
          <ShareButton
            color="#696969"
            icon={<IconCode viewBox="0 0 16 17" />}
            text={t("Embed")}
            onClick={handleCopyEmbedCode}
          />
        )}

        <ShareButton
          color="#5BD4E5"
          icon={<IconCopy viewBox="0 0 12 12" />}
          text={t("Video link")}
          onClick={handleCopyLink}
        />
      </ShareButtons>

      {contextHolder}
    </ContentContainer>
  );
};

const ContentContainer = styled.div`
  padding: 25px 20px;
  flex-direction: column;
  align-items: flex-start;

  width: 380px;
  max-height: calc(100vh - 70px);
  overflow: auto;
  box-sizing: border-box;

  border-radius: 10px;
  background: #fff;
  box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.25);
`;

const Title = styled.div`
  margin-bottom: 10px;

  color: #222;
  font-family: ${poppins.string};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const Description = styled.div`
  margin-bottom: 20px;

  color: #000;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
`;

const ShareButton = ({
  color,
  icon,
  text,
  onClick,
}: {
  color: string;
  icon: ReactNode;
  text: string;
  onClick: () => void;
}) => {
  return (
    <ShareButtonContainer onClick={onClick} type="text">
      <ShareButtonIcon $backgroundColor={color}>{icon}</ShareButtonIcon>
      <ShareButtonText>{text}</ShareButtonText>
    </ShareButtonContainer>
  );
};

const ShareButtonContainer = styled(Button)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  height: auto;
  padding: 0;
`;

const ShareButtonIcon = styled.div<{ $backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 60px;
  height: 60px;
  border-radius: 9999px;
  background: ${({ $backgroundColor }) => $backgroundColor};

  svg {
    width: 24px;
    height: 24px;
  }
  svg path {
    stroke: #fff;
  }
`;

const ShareButtonText = styled.div`
  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
`;
